import { useEffect, useState } from 'react'
import { gql, ApolloLink } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'

import AppSyncClient, { createAppSyncLink, AUTH_TYPE } from 'aws-appsync'

import { onNewPrintJob, scaleForStation } from './hwConnect'
import { sendEvent } from './honeycomb'

const UNAUTHORIZED_HTTP_STATUS_CODE = 401
const MAX_SUBSCRIPTION_RETRIES = 10
const subscribeToPrintJobs = (client, ipc, computer, retryAttempt = 0) => {
  if (!computer) return
  if (!client) return
  return client
    .subscribe({
      query: gql`
        subscription OnNewJob($computer_id: Int!) {
          OnNewJob(computer_id: $computer_id) {
            _id
            station_id
            metadata
            created_at
            files {
              file
              type
              copies
            }
          }
        }
      `,
      client: client,
      variables: { computer_id: computer.row_id },
    })
    .subscribe({
      next({ data }) {
        retryAttempt = 0
        console.log('Received AppSync print request')
        onNewPrintJob(computer, data.OnNewJob, ipc)
      },
      error(err) {
        console.error('Error OnNewJob:', err)
        sendEvent(ipc, {
          name: 'print_error',
          account_id: computer.account_id,
          client_timestamp: Date.now(),
          error: err,
        })

        if (retryAttempt >= MAX_SUBSCRIPTION_RETRIES) return
        subscribeToPrintJobs(client, ipc, computer, retryAttempt + 1)
      },
    })
}

const subscribeToRequestHardware = (client, ipc, computer, updateComputer) => {
  if (!computer) return
  if (!client) return

  return client
    .subscribe({
      query: gql`
        subscription OnUpdateRequest($computer_id: Int!) {
          OnUpdateRequest(computer_id: $computer_id) {
            computer_id
          }
        }
      `,
      client: client,
      variables: { computer_id: computer.row_id },
    })
    .subscribe({
      next({ data }) {
        console.log('Requested hardware', computer, data)
        updateComputer()
      },
      error(err) {
        console.error('Error OnUpdateRequest:', err)
      },
    })
}

const subscribeToScaleJobs = (client, ipc, computer) => {
  if (!computer) return
  if (!client) return

  return client
    .subscribe({
      query: gql`
        subscription OnRequestScaleValue($computer_id: Int!) {
          OnRequestScaleValue(computer_id: $computer_id) {
            station_id
          }
        }
      `,
      client: client,
      variables: { computer_id: computer.row_id },
    })
    .subscribe({
      next({ data }) {
        console.log('Requested scale value', computer, data)
        const [station, scale] = scaleForStation(
          computer,
          data.OnRequestScaleValue.station_id,
        )

        if (!scale) {
          window.ipc.send('log', 'No scale selected for the station')
          return
        }

        const { weight_in_ounces, error } = ipc.sendSync('get-weight', scale)
        client.mutate({
          mutation: gql`
            mutation PutScaleValue(
              $station_id: Int!
              $value: Float
              $error: String
            ) {
              PutScaleValue(
                station_id: $station_id
                value: $value
                error: $error
              ) {
                value
                error
                account_id
                station_id
                computer_id
              }
            }
          `,
          client,
          variables: {
            station_id: station.row_id,
            value: weight_in_ounces,
            error: error,
          },
        })
      },
      error(err) {
        console.error('Error OnRequestScaleValue:', err)
      },
    })
}

const subscribeToStationChanges = (client, ipc, computer, refetch) => {
  if (!computer) return
  if (!client) return

  return client
    .subscribe({
      query: gql`
        subscription OnStationSettingsUpdated($computer_id: Int!) {
          OnStationSettingsUpdated(computer_id: $computer_id) {
            computer_id
          }
        }
      `,
      client: client,
      variables: { computer_id: computer.row_id },
    })
    .subscribe({
      next(data) {
        console.log('Station settings have changed', computer, data)
        refetch()
      },
      error(err) {
        console.error('Error OnStationSettingsUpdated:', err)
      },
    })
}

let client = null
const ClientFactory = ({ token, onLogout, url }) => {
  if (client) return client
  if (!url) return
  client = new AppSyncClient(
    { disableOffline: true },
    {
      link: ApolloLink.from([
        new RetryLink(),
        onError(({ graphQLErrors, networkError, ...rest }) => {
          console.error({ graphQLErrors, networkError, ...rest })
          if (
            networkError &&
            'statusCode' in networkError &&
            networkError.statusCode === UNAUTHORIZED_HTTP_STATUS_CODE
          ) {
            onLogout()
          }
        }),
        new ApolloLink((operation, next) => {
          operation.setContext({
            headers: { Authorization: `Bearer ${token}` },
          })
          return next(operation)
        }),
        createAppSyncLink({
          url: url,
          auth: {
            type: AUTH_TYPE.NONE,
            apiKey: '',
          },
          disableOffline: true,
        }),
      ]),
    },
  )

  return client
}

const maybeUnsub = obs => {
  if (obs) obs.unsubscribe()
}

export default (
  ipc,
  token,
  computer,
  logout,
  updateComputerFull,
  refetchComputer,
  printerServiceUrl,
) => {
  const [client, setClient] = useState(null)

  useEffect(() => {
    setClient(
      ClientFactory({
        token,
        onLogout: logout,
        url: printerServiceUrl,
      }),
    )
  }, [token, printerServiceUrl])

  const stations = JSON.stringify(
    computer ? computer.printer_stations.edges : null,
  )

  useEffect(() => {
    if (!client || !stations) {
      return
    }
    console.log('AppSync client ready')
    const printJob = subscribeToPrintJobs(client, ipc, computer)
    const scaleJob = subscribeToScaleJobs(client, ipc, computer)
    const requestHw = subscribeToRequestHardware(
      client,
      ipc,
      computer,
      async () => {
        updateComputerFull()
        computerUpdated()
      },
    )
    const stationChange = subscribeToStationChanges(
      client,
      ipc,
      computer,
      refetchComputer,
    )
    return () => {
      maybeUnsub(printJob)
      maybeUnsub(scaleJob)
      maybeUnsub(requestHw)
      maybeUnsub(stationChange)
    }
  }, [stations, client])

  if (!computer) return { computerUpdated: () => {} }

  const computerUpdated = () => {
    return client.mutate({
      mutation: gql`
        mutation ComputerUpdated($computer_id: Int) {
          ComputerUpdated(computer_id: $computer_id) {
            account_id
            computer_id
          }
        }
      `,
      client,
      variables: { computer_id: computer.row_id },
    })
  }

  return {
    computerUpdated,
  }
}
